import { Link } from "react-router-dom";
import { Sparkles, Zap, Infinity, Settings, CreditCard, LogOut, ChevronDown } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription, SUBSCRIPTION_TIERS } from "@/hooks/useSubscription";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import UserAvatar from "@/components/UserAvatar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface MobileHeaderProps {
  generationsLeft?: number;
}

const MobileHeader = ({ generationsLeft = 5 }: MobileHeaderProps) => {
  const { user, signOut } = useAuth();
  const { subscribed, tier } = useSubscription();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    toast.success("Signed out successfully");
    navigate("/");
  };

  return (
    <header className="lg:hidden fixed top-0 left-0 right-0 z-50 border-b border-border bg-card/95 backdrop-blur-xl">
      <div className="flex items-center justify-between px-4 h-14">
        {/* Logo */}
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg gradient-bg flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-primary-foreground" />
          </div>
          <span className="text-lg font-bold gradient-text">ThumbGenius</span>
        </Link>

        <div className="flex items-center gap-3">
          {/* Credits Badge */}
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${
            subscribed ? 'bg-green-500/10 border border-green-500/30' : 'bg-secondary border border-border'
          }`}>
            {subscribed ? (
              <>
                <Infinity className="w-3.5 h-3.5 text-green-500" />
                <span className="text-green-400">Illimité</span>
              </>
            ) : (
              <>
                <Zap className="w-3.5 h-3.5 text-primary" />
                <span>{generationsLeft} left</span>
              </>
            )}
          </div>

          {/* Profile Popover */}
          <Popover>
            <PopoverTrigger asChild>
              <button className="flex items-center gap-1.5 p-1 rounded-lg hover:bg-secondary transition-colors">
                <UserAvatar
                  avatarUrl={user?.user_metadata?.avatar_url}
                  name={user?.user_metadata?.full_name}
                  tier={tier}
                  size="sm"
                />
                <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="bottom"
              align="end"
              sideOffset={8}
              className="w-56 p-1.5 bg-card border border-border rounded-xl shadow-xl"
            >
              <div className="px-3 py-2 mb-1">
                <p className="font-medium text-sm truncate">
                  {user?.user_metadata?.full_name || "User"}
                </p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
              <div className="h-px bg-border mb-1" />
              <div className="space-y-0.5">
                <Link
                  to="/dashboard"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                >
                  <Sparkles className="w-4 h-4" />
                  <span>Dashboard</span>
                </Link>
                <Link
                  to="/my-thumbnails"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                >
                  <Sparkles className="w-4 h-4" />
                  <span>My Thumbnails</span>
                </Link>
                <Link
                  to="/analytics"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                >
                  <Sparkles className="w-4 h-4" />
                  <span>Analytics</span>
                </Link>
                <div className="h-px bg-border my-1" />
                <Link
                  to="/settings"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  <span>Paramètres</span>
                </Link>
                <Link
                  to="/billing"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                >
                  <CreditCard className="w-4 h-4" />
                  <span>Facturation</span>
                </Link>
                <div className="h-px bg-border my-1" />
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Déconnexion</span>
                </button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </header>
  );
};

export default MobileHeader;
